/**
 * The offline outbox — class B, the money path.
 *
 * Specification: ../test/outbox.spec.ts. Master plan §3 K4, §2 D6.
 *
 * Class B is money: orders, tenders, tips, voids, punches. It is the one data
 * class we own outright, and deliberately so. CRDTs guarantee CONVERGENCE, not
 * CORRECTNESS — two devices concurrently discounting a ticket converge on a
 * value nobody chose, and the CRDT cannot even tell you a conflict happened.
 * Money conflicts must be visible and resolvable.
 *
 * What replaces them: an append-only local log, this durable blocking FIFO,
 * HLC for causal ordering, and idempotent server ingest that assigns the
 * authoritative sequence.
 *
 * The real store is wa-sqlite on OPFS with `PRAGMA synchronous = FULL`. The
 * store is a port so the logic can be tested without a browser, and because
 * the venue hub runs the same code against a different backing.
 */

/** What a device sends. Mirrors the kernel's SubmittedEvent minus server bits. */
export interface OutboundEvent {
  /** Client-generated UUIDv7. The idempotency key for the whole path. */
  readonly eventId: string;
  /**
   * The aggregate this event belongs to.
   *
   * Every event of one order shares it. Without it the uploader had to guess —
   * it fell back to the event's own id, so each event became a stream of one,
   * and a fold over "the order" would have found nothing to fold.
   */
  readonly streamId: string;
  readonly type: string;
  readonly payload: unknown;
  readonly hlc: string;
  readonly occurredAt: Date;
  /**
   * WHO DID IT.
   *
   * `PosSession` has carried one since it was written and the PIN sign-in
   * exists for no other reason than to populate it — and there was no field
   * here, so it never left the device. Every event this platform had ingested
   * carried `actor_id = NULL`, which makes void-abuse detection, the fraud
   * statistics and the dual-principal audit chain an attribution every row
   * shares, and therefore no attribution at all.
   *
   * Optional because not every event has one: a device-initiated sync or a
   * config refresh is the platform acting, not a person.
   */
  readonly actorId?: string | null;
}

export type RecordState = "pending" | "quarantined";

/**
 * Whether this device rang the event or is merely holding it for a peer.
 *
 * Load-bearing: a peer must never re-export what it holds on someone else's
 * behalf, or a venue full of devices gossips copies of copies and the tail
 * nobody owns never drains.
 */
export type RecordOrigin = "local" | "peer";

export interface OutboxRecord {
  readonly eventId: string;
  /** Local monotonic order. The FIFO is over this, not over timestamps. */
  readonly seq: number;
  readonly event: OutboundEvent;
  readonly enqueuedAt: number;
  readonly attempts: number;
  readonly lastError: string | null;
  readonly state: RecordState;
  readonly origin: RecordOrigin;
}

export interface OutboxStore {
  append(record: OutboxRecord): Promise<void>;
  has(eventId: string): Promise<boolean>;
  /** Oldest pending first. */
  head(limit: number): Promise<OutboxRecord[]>;
  remove(eventIds: readonly string[]): Promise<void>;
  update(records: readonly OutboxRecord[]): Promise<void>;
  all(): Promise<OutboxRecord[]>;
  nextSeq(): Promise<number>;
}

/**
 * Reference implementation and test double.
 *
 * Also the shape the OPFS store must match, which is why it is exported rather
 * than living in the test file.
 */
export class MemoryOutboxStore implements OutboxStore {
  readonly #records = new Map<string, OutboxRecord>();
  #seq = 0;

  async append(record: OutboxRecord): Promise<void> {
    this.#records.set(record.eventId, record);
  }

  async has(eventId: string): Promise<boolean> {
    return this.#records.has(eventId);
  }

  async head(limit: number): Promise<OutboxRecord[]> {
    return [...this.#records.values()]
      .filter((r) => r.state === "pending")
      .sort((a, b) => a.seq - b.seq)
      .slice(0, limit);
  }

  async remove(eventIds: readonly string[]): Promise<void> {
    for (const id of eventIds) this.#records.delete(id);
  }

  async update(records: readonly OutboxRecord[]): Promise<void> {
    for (const record of records) this.#records.set(record.eventId, record);
  }

  async all(): Promise<OutboxRecord[]> {
    return [...this.#records.values()].sort((a, b) => a.seq - b.seq);
  }

  async nextSeq(): Promise<number> {
    const highest = [...this.#records.values()].reduce((acc, r) => Math.max(acc, r.seq), this.#seq);
    this.#seq = highest + 1;
    return this.#seq;
  }
}

export interface UploadResult {
  readonly accepted: readonly string[];
  /** Already known to the server. Acknowledge exactly like an accept. */
  readonly duplicates: readonly string[];
  /** Permanently unacceptable. Retried a bounded number of times, then held. */
  readonly rejected: readonly { readonly eventId: string; readonly reason: string }[];
}

export type Uploader = (batch: readonly OutboundEvent[]) => Promise<UploadResult>;

export interface OutboxOptions {
  readonly batchSize: number;
  /** Attempts before an event is quarantined and stepped over. */
  readonly maxAttempts: number;
}

export interface QuarantinedEvent {
  readonly eventId: string;
  readonly attempts: number;
  readonly lastError: string | null;
  readonly record: OutboundEvent;
}

export interface DrainResult {
  readonly accepted: readonly string[];
  readonly duplicates: readonly string[];
  readonly quarantined: readonly string[];
  /** Set when the batch never reached the server. Null on a clean round trip. */
  readonly transportError: string | null;
  readonly remaining: number;
}

export class Outbox {
  readonly #store: OutboxStore;
  readonly #options: OutboxOptions;

  constructor(store: OutboxStore, options: OutboxOptions) {
    this.#store = store;
    this.#options = options;
  }

  async enqueue(event: OutboundEvent, origin: RecordOrigin = "local"): Promise<void> {
    // Dedup locally as well as at the server. A double tap on Send must not
    // become two tickets, and the event id is already the idempotency key.
    if (await this.#store.has(event.eventId)) return;

    await this.#store.append({
      eventId: event.eventId,
      seq: await this.#store.nextSeq(),
      event,
      enqueuedAt: event.occurredAt.getTime(),
      attempts: 0,
      lastError: null,
      state: "pending",
      origin,
    });
  }

  async depth(): Promise<number> {
    return (await this.#store.all()).filter((r) => r.state === "pending").length;
  }

  /**
   * The queued events with their attempt counts.
   *
   * Operationally this is what a support screen shows when a device will not
   * drain: how many events, how many tries each, and the last error the server
   * gave. It is also what lets a test assert that a network outage did not
   * quietly consume anybody's attempt budget.
   */
  async pending(): Promise<OutboxRecord[]> {
    return (await this.#store.all())
      .filter((r) => r.state === "pending")
      .sort((a, b) => a.seq - b.seq);
  }

  async quarantined(): Promise<QuarantinedEvent[]> {
    return (await this.#store.all())
      .filter((r) => r.state === "quarantined")
      .map((r) => ({
        eventId: r.eventId,
        attempts: r.attempts,
        lastError: r.lastError,
        record: r.event,
      }));
  }

  /**
   * Attempt ONE batch from the head.
   *
   * One batch per call, not a loop: the caller owns the retry cadence and the
   * backoff, and a self-looping drain would burn an event's whole attempt
   * budget inside a single call — turning one transient server hiccup into a
   * quarantine.
   *
   * The FIFO blocks because only acknowledged events are removed: anything the
   * server did not take stays at the head and goes again next time. That is
   * the point — a void must never overtake the sale it reverses.
   */
  async drain(upload: Uploader): Promise<DrainResult> {
    const batch = await this.#store.head(this.#options.batchSize);
    if (batch.length === 0) {
      return {
        accepted: [],
        duplicates: [],
        quarantined: [],
        transportError: null,
        remaining: 0,
      };
    }

    let result: UploadResult;
    try {
      result = await upload(batch.map((r) => r.event));
    } catch (error) {
      // Never reached the server. Nothing is acknowledged and nothing counts
      // against an event's attempt budget — the network being down is not the
      // event's fault, and counting it would quarantine a whole shift's work
      // during one outage.
      return {
        accepted: [],
        duplicates: [],
        quarantined: [],
        transportError: error instanceof Error ? error.message : String(error),
        remaining: await this.depth(),
      };
    }

    const acknowledged = [...result.accepted, ...result.duplicates];
    await this.#store.remove(acknowledged);

    const rejections = new Map(result.rejected.map((r) => [r.eventId, r.reason]));
    const quarantinedIds: string[] = [];
    const updates: OutboxRecord[] = [];

    for (const record of batch) {
      const reason = rejections.get(record.eventId);
      if (reason === undefined) continue;

      const attempts = record.attempts + 1;
      // One poisoned event blocking the queue forever costs the venue an
      // entire evening's takings. After a bounded number of tries it is held
      // — preserved in full, surfaced to an operator, and stepped over.
      //
      // Safe because events are union-mergeable FACTS and the server-side fold
      // defers an event whose prerequisite has not landed rather than
      // rejecting it. A skipped event is a gap somebody can repair, not a
      // corruption.
      const state: RecordState = attempts >= this.#options.maxAttempts ? "quarantined" : "pending";
      if (state === "quarantined") quarantinedIds.push(record.eventId);
      updates.push({ ...record, attempts, lastError: reason, state });
    }

    if (updates.length > 0) await this.#store.update(updates);

    return {
      accepted: result.accepted,
      duplicates: result.duplicates,
      quarantined: quarantinedIds,
      transportError: null,
      remaining: await this.depth(),
    };
  }
}

// --- Peer replication ------------------------------------------------------

export interface ImportResult {
  readonly imported: number;
  readonly alreadyPresent: number;
}

/**
 * The tail this device OWNS, for handing to a peer in the venue.
 *
 * A stolen, drowned or flat tablet must not be a lost evening, and the other
 * till is right there. Only local-origin records are exported: a peer
 * re-exporting what it holds for someone else makes the venue gossip copies of
 * copies, and the tail nobody owns never drains.
 */
export async function exportTail(store: OutboxStore): Promise<OutboxRecord[]> {
  return (await store.all()).filter((r) => r.origin === "local" && r.state === "pending");
}

/**
 * Take a peer's tail.
 *
 * Idempotent, because peers gossip repeatedly — and safe to upload from either
 * side, because the server deduplicates on the event id and reports the loser
 * as a duplicate, which the outbox treats as success.
 */
export async function importTail(
  store: OutboxStore,
  records: readonly OutboxRecord[],
): Promise<ImportResult> {
  let imported = 0;
  let alreadyPresent = 0;

  for (const record of records) {
    if (await store.has(record.eventId)) {
      alreadyPresent++;
      continue;
    }
    await store.append({
      ...record,
      seq: await store.nextSeq(),
      // Marked as held for someone else, so it is uploaded but never
      // re-gossiped.
      origin: "peer",
    });
    imported++;
  }

  return { imported, alreadyPresent };
}
